/**
 * Möteslistan: Mina möten / Delade med mig / Alla (admin).
 *
 * "Alla" visar varje möte admin får se. Möten admin inte har åtkomst till står
 * med rubrik, datum, typ, ägare och status – utan länk, eftersom innehållet
 * inte går att öppna – och kan raderas.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SegmentedControl, Spinner } from '@/components/ui';
import type { MotesListrad } from '@/lib/api';
import { cn } from '@/lib/utils';
import { StatusPiller } from './MotesDelar';
import { KALLA_ETIKETT, TYP_ETIKETT, datumText } from './motesText';

type Vy = 'mina' | 'delade' | 'alla';

interface Props {
  rader: MotesListrad[] | null;
  laddar: boolean;
  fel: string | null;
  arAdmin: boolean;
  onRadera: (rad: MotesListrad) => void;
}

const TOMT: Record<Vy, string> = {
  mina: 'Du har inga möten än. Börja med Nytt möte.',
  delade:
    'Inga möten är delade med dig än. Interna möten för alla och möten där du lagts till som deltagare hamnar här.',
  alla: 'Det finns inga möten än.',
};

export function MotesLista({ rader, laddar, fel, arAdmin, onRadera }: Props) {
  const [vy, setVy] = useState<Vy>('mina');
  const alla = rader ?? [];
  const mina = alla.filter((r) => r.relation === 'egen');
  const delade = alla.filter((r) => r.relation === 'delad');
  const synliga = vy === 'mina' ? mina : vy === 'delade' ? delade : alla;

  const segment = [
    { value: 'mina' as const, label: 'Mina möten', count: mina.length },
    { value: 'delade' as const, label: 'Delade med mig', count: delade.length },
    ...(arAdmin ? [{ value: 'alla' as const, label: 'Alla', count: alla.length }] : []),
  ];

  return (
    <section aria-labelledby="motesl-rubrik" className="rounded-xl border border-ink-200 bg-surface shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-5 pb-4">
        <h2 id="motesl-rubrik" className="text-[15px] font-bold text-ink-900">
          Möten
        </h2>
        <SegmentedControl label="Visa möten" segments={segment} value={vy} onChange={setVy} />
      </div>

      {laddar && rader === null ? (
        <div className="flex justify-center py-12">
          <Spinner size={22} className="text-brand-500" label="Hämtar möten" />
        </div>
      ) : fel ? (
        <div
          role="alert"
          className="mx-5 mb-5 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-[13px] text-red-800"
        >
          {fel}
        </div>
      ) : (
        <>
          {vy === 'alla' && alla.some((r) => r.atkomst === 'metadata') && (
            <p className="px-5 pb-3 text-[12.5px] text-ink-600 leading-relaxed">
              Möten som inte är delade med dig visas utan länk: du ser rubrik, datum, typ, ägare och status, och kan
              radera dem – men inte läsa innehållet.
            </p>
          )}
          {synliga.length === 0 ? (
            <p className="px-5 pb-8 pt-2 text-[13px] text-ink-500">{TOMT[vy]}</p>
          ) : (
            <ul className="border-t border-ink-200 divide-y divide-ink-200">
              {synliga.map((r) => (
                <li key={r.id}>
                  <Rad rad={r} visaAgare={r.relation !== 'egen'} onRadera={onRadera} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function Rad({
  rad,
  visaAgare,
  onRadera,
}: {
  rad: MotesListrad;
  visaAgare: boolean;
  onRadera: (r: MotesListrad) => void;
}) {
  const datum = datumText(rad.motesdatum ?? rad.skapad_at);
  const meta = [datum, TYP_ETIKETT[rad.typ], KALLA_ETIKETT[rad.kalla], visaAgare && rad.agare_epost]
    .filter(Boolean)
    .join(' · ');
  const innehall = (
    <>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-ink-900 truncate">{rad.titel}</p>
        <p className="text-[12.5px] text-ink-500 mt-0.5 break-words">{meta}</p>
      </div>
      <StatusPiller status={rad.status} kalla={rad.kalla} fel={rad.fel} className="shrink-0 self-start sm:self-auto" />
    </>
  );

  if (rad.atkomst === 'allt') {
    return (
      <Link
        to={`/moten/${rad.id}`}
        className={cn(
          'flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-5 py-3.5 transition-colors hover:bg-ink-50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
        )}
      >
        {innehall}
      </Link>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-5 py-3.5">
      {innehall}
      <button
        type="button"
        onClick={() => onRadera(rad)}
        className="self-start sm:self-auto h-8 px-3 rounded-lg text-[12.5px] font-semibold text-ink-600 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        aria-label={`Radera ${rad.titel}`}
      >
        Radera
      </button>
    </div>
  );
}
